import React from 'react';
import { IRoute, IRoutePolicy, Material } from '../../sp-types';
import { IBalanceFlow } from '../../sp-types/balanceFlowType';
import { LinearGauge } from '@UI-Elements/Charts/LinearGauge/LinearGauge';
import ButtonDesigned from '@UI-Elements/Buttons/ButtonDesigned';
import { LoadingSpinnerOverlay } from '@UI-Elements/LoadingSpinnerOverlay';
import { createEmptyRiskTolerance, getRiskTolerance } from '../../utils/accountUtils';
import { Link, useNavigate } from 'react-router-dom';
import { useAccountStore } from '../../components/Stores/accountStore';
import { FormatToShortPriceTemplate } from '../../utils/FormatValue';
import { RouteDetailsPopup } from '../Policy/MaterialDetails/components/RouteDetailPopup';
import { getBalanceArrowTypeLocation, getExecutionInstructions } from './balanceUtils';
import { getRoutePoliciesByMaterial } from '../../sp-firebase/routePolicyFB';
import { sortByOrder } from '../Policy/MaterialDetails/components/MaterialDetailUtils';
import { getRoutesOfMaterial } from '../../sp-firebase/routesFB';
import { getExposureColor, getExposureStatus } from '../../utils/RiskUtils';
import { mergeRoutesAndPolicies } from '@utils/routesUtils';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DroppableList } from './EditRoutesComponents';

interface Props {
  routes: IRoute[];
  balanceFlowData: IBalanceFlow;
  updateBalance: (_balanceId: string, _updatedBalance: IBalanceFlow) => Promise<void>;
  materialsMap: Record<string, Material>;
}
export const EditRoutes: React.FC<Props> = ({ routes, balanceFlowData, updateBalance, materialsMap }) => {
  const navigate = useNavigate();
  const { materialId, exposureBeforeBalance, accountId } = balanceFlowData;
  const { account } = useAccountStore();
  const [isLoading, setIsLoading] = React.useState(false);
  const [routePolicies, setRoutePolicies] = React.useState<IRoutePolicy[] | undefined>();
  const [materialRoutes, setMaterialRoutes] = React.useState<IRoute[] | undefined>();
  const [balanceRoutes, setBalanceRoutes] = React.useState<IRoute[] | undefined>(balanceFlowData.orderedRoutes);
  //check if needed
  React.useEffect(() => {
    if (balanceFlowData.orderedRoutes) {
      setBalanceRoutes(balanceFlowData.orderedRoutes);
    }
  }, [balanceFlowData.orderedRoutes]);

  const [showPopup, setShowPopup] = React.useState<IRoute | null>(null);
  const [balanceArrowData, setBalanceArrowData] = React.useState<{ value: number; img: string } | undefined>();

  async function fetchRoutePolicies() {
    const routePoles = (await getRoutePoliciesByMaterial(accountId, materialId)) || [];
    const routes = (await getRoutesOfMaterial(materialId)) || [];
    setRoutePolicies(routePoles.sort(sortByOrder));
    setMaterialRoutes(routes);
    //console.debug('filtering', routes, balanceRoutes);
  }

  React.useEffect(() => {
    if (accountId && materialId) {
      fetchRoutePolicies();
    }
  }, [accountId, materialId]);

  React.useEffect(() => {
    setBalanceArrowData(getBalanceArrowTypeLocation(account, materialId, balanceFlowData));
  }, [balanceFlowData, exposureBeforeBalance, materialId]);

  const updateRoutes = async () => {
    setIsLoading(true);

    const balanceInstructions = await getExecutionInstructions(balanceFlowData.materialExposure, balanceRoutes || []);
    const orderedRoutes = mergeRoutesAndPolicies(balanceRoutes || [], [], balanceInstructions);

    await updateBalance(balanceFlowData.id, {
      ...balanceFlowData,
      orderedRoutes,
      balanceInstructions,
    });

    navigate('..');
  };

  const getColor = (netExposure: number): string =>
    getExposureColor(
      getExposureStatus(getRiskTolerance(materialId, account) || createEmptyRiskTolerance(), netExposure),
    );

  const removeSelectedRoutes = (allRoutes: IRoute[], selectedRoutes: IRoute[]): IRoute[] => {
    const routesIds = new Set<string>(selectedRoutes.map((a) => a.id || ''));

    return allRoutes.filter((r) => !routesIds.has(r.id || ''));
  };

  const removeDuplicates = (routes: IRoute[]): IRoute[] =>
    Object.values(
      routes.reduce((acc, route) => ({ ...acc, [route.id]: acc[route.id] || route }), {} as Record<string, IRoute>),
    );

  const onChangeRoutes = (routes: IRoute[], listType: 'all' | 'selected') => {
    if (listType === 'selected') {
      setBalanceRoutes(routes);
    }
    if (listType === 'all') {
      setMaterialRoutes(routes);
    }
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="flex flex-1 gap-3 overflow-hidden min-h-[70vh]">
        <div className="flex flex-1 flex-col min-w-[40vw]">
          <DroppableList
            title="Available routes"
            routes={removeSelectedRoutes(materialRoutes || [], balanceRoutes || [])}
            listType="all"
            otherList={balanceRoutes || []}
            onChangeRoutes={onChangeRoutes}
            showPopup={(route: IRoute) => setShowPopup(route)}
            materialsMap={materialsMap}
          ></DroppableList>
        </div>
        <div className="flex flex-1 flex-col gap-3 bg-white p-4 h-full">
          <div className="flex flex-col ">
            <h2 className="text-lg ">Details</h2>
            <p className="text-sm text-secondary">
              You can modify the list by removing the existing route and dragging a new one.
            </p>
          </div>

          <h6 className="text-red py-1">Expected balance</h6>
          <div className="flex lg:gap-5 xl:gap-20 my-2">
            <div className="flex flex-col flex-0 items-center justify-center h-full gap-2">
              <p className="text-xs text-gray-450">Net exposure</p>
              <p className="text-xl" style={{ color: getColor(exposureBeforeBalance) }}>
                {FormatToShortPriceTemplate(exposureBeforeBalance)}
              </p>
            </div>
            <div className="flex flex-col flex-1 max-w-[30vw]">
              <LinearGauge
                isZoomExposure
                hightInRem={1.5}
                currentValue={exposureBeforeBalance}
                riskTolerance={getRiskTolerance(materialId, account) || createEmptyRiskTolerance()}
                numberOfTicks={12}
                {...(balanceArrowData !== undefined
                  ? { additionalArrows: [{ img: balanceArrowData.img, value: balanceArrowData.value }] }
                  : {})}
              />
            </div>
          </div>
          <div className="flex flex-1 flex-col items-start justify-start xl:max-w-[40vw]">
            <div className="text-red ">Routes</div>
            <div className="overflow-y-auto w-full max-h-[20vh] xl:max-h-[30vh] 2xl:max-h-[50vh]">
              <DroppableList
                routes={removeDuplicates(balanceRoutes || [])}
                listType="selected"
                otherList={materialRoutes || []}
                onChangeRoutes={onChangeRoutes}
                showPopup={(route: IRoute) => setShowPopup(route)}
                dropMessage="Drag your preferred routes in here"
                materialsMap={materialsMap}
              ></DroppableList>
            </div>

            {showPopup && (
              <RouteDetailsPopup onClose={() => setShowPopup(null)} route={showPopup} isDarkShade isFullScreen />
            )}
          </div>
          <div className="flex sticky bottom-0 *bg-white gap-3">
            <Link to={'..'} className="">
              <ButtonDesigned className="py-2 " style="outline" onButtonClick={() => 1}>
                Cancel
              </ButtonDesigned>
            </Link>
            <ButtonDesigned disableButton={!routes} onButtonClick={updateRoutes}>
              Update
            </ButtonDesigned>
          </div>
        </div>
        {isLoading && <LoadingSpinnerOverlay size={64} />}
      </div>
    </DndProvider>
  );
};
